import { type IsoMonth, toRetPeriod } from './calendar';
import { type Gstr3bComputation, type ReverseChargeEntry, reverseChargeTax } from './gstr3b';
import type { Paise } from './money';
import { addHeads, type ByHead, NO_TAX, TAX_HEADS } from './setoff';

/**
 * GSTR-3B as the GST portal takes it (Phase 11), in GSTN's JSON (GSTR-3B API v7.1; amounts in rupees):
 * the return saved before any money moves (RETSAVE), how the tax is paid once the cash is in the ledger
 * (RETOFFSET), and the tax liability breakup confirmed after that (RETBKP).
 *
 * Tables 3.1(a), (b), (c) and (e) and the credit in Table 4A come from GSTR-1 and GSTR-2B and are locked
 * on the portal: they go back as the portal filled them. EkApp adds what only the person knows:
 * reverse-charge purchases (3.1(d); their credit in 4A(2) for services from abroad, 4A(3) otherwise) and
 * credit to reverse (4B(1) for good, 4B(2) until the supplier is paid). Table 5.1 is the previous month's
 * interest and late fee as the portal worked them out (it can't be lowered). Table 3.2 isn't sent: the
 * portal fills it from GSTR-1 and locks it.
 */

/** The tables the portal fills in for a month, as they're saved back. */
export interface Gstr3bAutoTables {
  /** 3.1(a): outward taxable supplies (other than zero-rated, nil-rated and exempt). */
  outward: { taxable: Paise; tax: ByHead };
  /** 3.1(b): zero-rated supplies (exports, SEZ): IGST and cess only. */
  zeroRated: { taxable: Paise; tax: ByHead };
  /** 3.1(c): nil-rated and exempt; 3.1(e): non-GST. Taxable values only. */
  nilExempt: Paise;
  nonGst: Paise;
  /** 4A(1) imports of goods; 4A(4) from an input service distributor; 4A(5) all other credit (GSTR-2B). */
  credit: { imports: ByHead; isd: ByHead; other: ByHead };
}

export const NO_AUTO_TABLES: Gstr3bAutoTables = Object.freeze({
  outward: { taxable: 0, tax: NO_TAX },
  zeroRated: { taxable: 0, tax: NO_TAX },
  nilExempt: 0,
  nonGst: 0,
  credit: { imports: NO_TAX, isd: NO_TAX, other: NO_TAX },
});

type Money = number;
interface Amounts {
  txval?: Money;
  iamt: Money;
  camt: Money;
  samt: Money;
  csamt: Money;
}

const rupees = (paise: Paise): Money => Math.round(paise) / 100;
const amounts = (tax: ByHead, taxable?: Paise): Amounts => ({
  ...(taxable === undefined ? {} : { txval: rupees(taxable) }),
  iamt: rupees(tax.igst),
  camt: rupees(tax.cgst),
  samt: rupees(tax.sgst),
  csamt: rupees(tax.cess),
});

export interface Gstr3bJson {
  gstin: string;
  ret_period: string;
  sup_details: {
    osup_det: Amounts;
    osup_zero: { txval: Money; iamt: Money; csamt: Money };
    osup_nil_exmp: { txval: Money };
    isup_rev: Amounts;
    osup_nongst: { txval: Money };
  };
  itc_elg: {
    itc_avl: ({ ty: 'IMPG' | 'IMPS' | 'ISRC' | 'ISD' | 'OTH' } & Amounts)[];
    itc_rev: ({ ty: 'RUL' | 'OTH' } & Amounts)[];
    itc_net: Amounts;
    itc_inelg: ({ ty: 'RUL' | 'OTH' } & Amounts)[];
  };
  intr_ltfee: {
    intr_details: Amounts;
    ltfee_details: { camt: Money; samt: Money };
  };
}

export interface Gstr3bJsonInput {
  gstin: string;
  period: IsoMonth;
  tables: Gstr3bAutoTables;
  /** What GSTR-2B shows can't be claimed (4D(2)). */
  ineligible: ByHead;
  reverseCharge: readonly ReverseChargeEntry[];
  /** EkApp's working of the month (packages/gst's gstr3b): reversals, net credit, Table 5.1. */
  computation: Gstr3bComputation;
}

/** GSTR-3B to save on the portal (RETSAVE). */
export function gstr3bJson(input: Gstr3bJsonInput): Gstr3bJson {
  const { tables, computation: c } = input;
  const fromAbroad = input.reverseCharge.filter((e) => e.kind === 'import_services');
  const athome = input.reverseCharge.filter((e) => e.kind !== 'import_services');
  const rcmTaxable = input.reverseCharge.reduce((sum, e) => sum + e.taxableValue, 0);
  return {
    gstin: input.gstin,
    ret_period: toRetPeriod(input.period),
    sup_details: {
      osup_det: amounts(tables.outward.tax, tables.outward.taxable),
      osup_zero: {
        txval: rupees(tables.zeroRated.taxable),
        iamt: rupees(tables.zeroRated.tax.igst),
        csamt: rupees(tables.zeroRated.tax.cess),
      },
      osup_nil_exmp: { txval: rupees(tables.nilExempt) },
      isup_rev: amounts(c.reverseCharge, rcmTaxable),
      osup_nongst: { txval: rupees(tables.nonGst) },
    },
    itc_elg: {
      itc_avl: [
        { ty: 'IMPG', ...amounts(tables.credit.imports) },
        { ty: 'IMPS', ...amounts(addHeads(NO_TAX, ...fromAbroad.map(reverseChargeTax))) },
        { ty: 'ISRC', ...amounts(addHeads(NO_TAX, ...athome.map(reverseChargeTax))) },
        { ty: 'ISD', ...amounts(tables.credit.isd) },
        { ty: 'OTH', ...amounts(tables.credit.other) },
      ],
      itc_rev: [
        { ty: 'RUL', ...amounts(c.credit.reversedForGood) },
        { ty: 'OTH', ...amounts(c.credit.reversedForNow) },
      ],
      itc_net: amounts(c.credit.net),
      itc_inelg: [
        { ty: 'RUL', ...amounts(NO_TAX) },
        { ty: 'OTH', ...amounts(input.ineligible) },
      ],
    },
    intr_ltfee: {
      intr_details: amounts(c.previous.interest),
      ltfee_details: { camt: rupees(c.previous.lateFee.cgst), samt: rupees(c.previous.lateFee.sgst) },
    },
  };
}

/** GSTN's liability ledger entries in Table 6.1: tax other than reverse charge, and reverse charge. */
export const TRANS_FORWARD = 30002;
export const TRANS_REVERSE = 30003;

export interface Gstr3bOffsetJson {
  pdcash: Record<string, number>[];
  pditc: Record<string, number>;
  nettaxpay: Record<string, unknown>[];
}

/**
 * How the month's GSTR-3B is paid (RETOFFSET), from EkApp's set-off: credit used in the order the law
 * sets (`pditc`: i_pdc is IGST credit used for CGST, and so on) and the rest from the cash ledger
 * (`pdcash`: tax by head, the previous month's interest and late fee), reverse charge in cash on its own
 * line. `ledgerIds` are the portal's liability ledger ids for the two lines, from its summary.
 */
export function gstr3bOffsetJson(
  c: Gstr3bComputation,
  ledgerIds: { forward: number | null; reverse: number | null } = { forward: null, reverse: null },
): Gstr3bOffsetJson {
  const used = (from: string, to: string) =>
    rupees(c.setOff.used.filter((u) => u.from === from && u.to === to).reduce((s, u) => s + u.amount, 0));
  const forwardCash = Object.fromEntries(
    TAX_HEADS.map((h) => [h, Math.max(0, c.setOff.cashTax[h] - c.reverseCharge[h])]),
  ) as Record<(typeof TAX_HEADS)[number], Paise>;
  const id = (value: number | null): Record<string, number> => (value === null ? {} : { liab_ldg_id: value });
  const forward = {
    ...id(ledgerIds.forward),
    trans_typ: TRANS_FORWARD,
    ipd: rupees(forwardCash.igst),
    cpd: rupees(forwardCash.cgst),
    spd: rupees(forwardCash.sgst),
    cspd: rupees(forwardCash.cess),
    i_intrpd: rupees(c.previous.interest.igst),
    c_intrpd: rupees(c.previous.interest.cgst),
    s_intrpd: rupees(c.previous.interest.sgst),
    cs_intrpd: rupees(c.previous.interest.cess),
    c_lfeepd: rupees(c.previous.lateFee.cgst),
    s_lfeepd: rupees(c.previous.lateFee.sgst),
  };
  const reverse = {
    ...id(ledgerIds.reverse),
    trans_typ: TRANS_REVERSE,
    ipd: rupees(c.reverseCharge.igst),
    cpd: rupees(c.reverseCharge.cgst),
    spd: rupees(c.reverseCharge.sgst),
    cspd: rupees(c.reverseCharge.cess),
  };
  const payable = (tax: ByHead, interest: ByHead, fee: ByHead) => ({
    igst: { tx: rupees(tax.igst), intr: rupees(interest.igst) },
    cgst: { tx: rupees(tax.cgst), intr: rupees(interest.cgst), fee: rupees(fee.cgst) },
    sgst: { tx: rupees(tax.sgst), intr: rupees(interest.sgst), fee: rupees(fee.sgst) },
    cess: { tx: rupees(tax.cess), intr: rupees(interest.cess) },
  });
  const hasReverse = TAX_HEADS.some((h) => c.reverseCharge[h] > 0);
  return {
    pdcash: hasReverse ? [forward, reverse] : [forward],
    pditc: {
      ...id(ledgerIds.forward),
      trans_typ: TRANS_FORWARD,
      i_pdi: used('igst', 'igst'),
      i_pdc: used('igst', 'cgst'),
      i_pds: used('igst', 'sgst'),
      c_pdi: used('cgst', 'igst'),
      c_pdc: used('cgst', 'cgst'),
      s_pdi: used('sgst', 'igst'),
      s_pds: used('sgst', 'sgst'),
      cs_pdcs: used('cess', 'cess'),
    },
    nettaxpay: [
      {
        ...id(ledgerIds.forward),
        trans_typ: TRANS_FORWARD,
        trans_desc: 'Other than reverse charge',
        ...payable(c.sales, c.previous.interest, c.previous.lateFee),
      },
      ...(hasReverse
        ? [
            {
              ...id(ledgerIds.reverse),
              trans_typ: TRANS_REVERSE,
              trans_desc: 'Reverse charge',
              ...payable(c.reverseCharge, NO_TAX, NO_TAX),
            },
          ]
        : []),
    ],
  };
}

/** What each head of cash the offset takes from the ledger: tax (forward and reverse charge), interest, fee. */
export function offsetCashNeeded(c: Gstr3bComputation): { tax: ByHead; interest: ByHead; fee: ByHead } {
  return { tax: c.setOff.cashTax, interest: c.previous.interest, fee: c.previous.lateFee };
}

export interface Gstr3bBreakupJson {
  gstin: string;
  ret_period: string;
  breakup: { ret_period: string; liability: Amounts }[];
}

/**
 * The tax liability breakup (RETBKP), confirmed after the offset: which period the tax paid belongs to.
 * EkApp's returns pay only their own month's tax, so it's all this month's (sales and reverse charge).
 */
export function gstr3bBreakupJson(gstin: string, period: IsoMonth, c: Gstr3bComputation): Gstr3bBreakupJson {
  const liability = addHeads(c.sales, c.reverseCharge);
  return {
    gstin,
    ret_period: toRetPeriod(period),
    breakup: [{ ret_period: toRetPeriod(period), liability: amounts(liability) }],
  };
}
